/**
 * Leviathan Web - Main Application Component
 * Quasar Prime LLC
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Auth
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { PermissionProvider } from '@/contexts/PermissionContext';
import { ProtectedRoute, AdminRoute } from '@components/auth/ProtectedRoute';

// Layouts
import DashboardLayout from '@components/layouts/DashboardLayout';

// Crash containment
import ErrorBoundary from '@components/ErrorBoundary';
import CrashTestPage from '@pages/dev/CrashTestPage';

// Auth Pages
import LoginPage from '@pages/auth/LoginPage';
import AcceptInvitePage from '@pages/auth/AcceptInvitePage';

// App Pages
import DashboardPage from '@pages/DashboardPage';
import CustomersPage from '@pages/customers/CustomersPage';
import NewCustomerPage from '@pages/customers/NewCustomerPage';
import CustomerWizard from '@pages/customers/CustomerWizard';
import CustomerDetailPage from '@pages/customers/CustomerDetailPage';
import CustomerLocations from '@pages/customers/CustomerLocations';
import CustomerLocationForm from '@pages/customers/CustomerLocationForm';
import CustomerLocationDetail from '@pages/customers/CustomerLocationDetail';
import CustomerLocationWizard from '@pages/customers/CustomerLocationWizard';
import InventoryPage from '@pages/inventory/InventoryPage';
import InventoryDetailPage from '@pages/inventory/InventoryDetailPage';
import InventoryFormPage from '@pages/inventory/InventoryFormPage';
import TaxClassAssignment from '@pages/inventory/TaxClassAssignment';
import TanksPage from '@pages/tanks/TanksPage';
import TankDetailPage from '@pages/tanks/TankDetailPage';
import TankFormPage from '@pages/tanks/TankFormPage';
import TankHistory from '@pages/tanks/TankHistory';
import OrdersPage from '@pages/orders/OrdersPage';
import OrderDetailPage from '@pages/orders/OrderDetailPage';
import OrderFormPage from '@pages/orders/OrderFormPage';
import DispatchPage from '@pages/dispatch/DispatchPage';
import DispatchDetailPage from '@pages/dispatch/DispatchDetailPage';
import DispatchFormPage from '@pages/dispatch/DispatchFormPage';
import DriverRoutePage from '@pages/dispatch/DriverRoutePage';
import ShipmentsPage from '@pages/shipments/ShipmentsPage';
import NewShipmentPage from '@pages/shipments/NewShipmentPage';
import ShipmentDetailPage from '@pages/shipments/ShipmentDetailPage';
import TrucksPage from '@pages/trucks/TrucksPage';
import CarriersPage from '@pages/carriers/CarriersPage';
import TruckDetailPage from '@pages/trucks/TruckDetailPage';
import TruckFormPage from '@pages/trucks/TruckFormPage';
import DriversPage from '@pages/drivers/DriversPage';
import DriverDetailPage from '@pages/drivers/DriverDetailPage';
import DriverFormPage from '@pages/drivers/DriverFormPage';
import SuppliersPage from '@pages/suppliers/SuppliersPage';
import SupplierDetailPage from '@pages/suppliers/SupplierDetailPage';
import SupplierFormPage from '@pages/suppliers/SupplierFormPage';
import ReceiptsPage from '@pages/receipts/ReceiptsPage';
import ReceiptDetailPage from '@pages/receipts/ReceiptDetailPage';
import ReceiptFormPage from '@pages/receipts/ReceiptFormPage';
import InvoicesPage from '@pages/invoices/InvoicesPage';
import InvoiceDetailPage from '@pages/invoices/InvoiceDetailPage';
import InvoiceFormPage from '@pages/invoices/InvoiceFormPage';
import AccountsReceivablePage from '@pages/invoices/AccountsReceivablePage';
import AccountsPayablePage from '@pages/ap/AccountsPayablePage';
import BillDetailPage from '@pages/ap/BillDetailPage';
import BillFormPage from '@pages/ap/BillFormPage';
import FinancialsPage from '@pages/financials/FinancialsPage';
import SettingsPage from '@pages/settings/SettingsPage';

// Pricing Pages
import PriceBooksPage from '@pages/pricing/PriceBooksPage';
import PriceBookDetailPage from '@pages/pricing/PriceBookDetailPage';
import PriceBookFormPage from '@pages/pricing/PriceBookFormPage';
import RackPricesPage from '@pages/pricing/RackPricesPage';
import TerminalRackPricesPage from '@pages/pricing/TerminalRackPricesPage';
import ContractsPage from '@pages/pricing/ContractsPage';
import ContractDetailPage from '@pages/pricing/ContractDetailPage';
import ContractFormPage from '@pages/pricing/ContractFormPage';

// Credit Management Pages
import CreditManagementPage from '@pages/CreditManagementPage';

// Tax Management Pages
import TaxRatesPage from '@pages/TaxRatesPage';
import TaxConfiguration from '@pages/settings/TaxConfiguration';
import TaxReadiness from '@pages/settings/TaxReadiness';
import TaxRateFormPage from '@pages/TaxRateFormPage';

// BOL Pages
import BOLListPage from '@pages/BOLListPage';
import BOLDetailPage from '@pages/BOLDetailPage';
import BOLPrintPage from '@pages/BOLPrintPage';

// Reports Pages
import ReportsPage from '@pages/reports/ReportsPage';
import ARAgingReport from '@pages/reports/ARAgingReport';
import SalesReport from '@pages/reports/SalesReport';
import DeliveryReport from '@pages/reports/DeliveryReport';
import TaxReport from '@pages/reports/TaxReport';
import StatementsPage from '@pages/reports/StatementsPage';
import StatementDetail from '@pages/reports/StatementDetail';
import StatementPrint from '@pages/reports/StatementPrint';

// Email Pages
import EmailTemplates from '@pages/settings/EmailTemplates';
import EmailTemplateForm from '@pages/settings/EmailTemplateForm';
import EmailLogs from '@pages/settings/EmailLogs';
import EmailSettingsPage from '@pages/settings/EmailSettingsPage';

// Audit & System Settings Pages
import AuditLogs from '@pages/settings/AuditLogs';
import SystemSettings from '@pages/settings/SystemSettings';
import ConfigurationSettings from '@pages/settings/ConfigurationSettings';
import AppearanceSettingsPage from '@pages/settings/AppearanceSettingsPage';

// Sales / CRM Pages
import SalesDashboard from '@pages/sales/SalesDashboard';
import LeadsList from '@pages/sales/LeadsList';
import LeadDetail from '@pages/sales/LeadDetail';
import LeadForm from '@pages/sales/LeadForm';
import ActivitiesList from '@pages/sales/ActivitiesList';
import QuotesList from '@pages/sales/QuotesList';
import QuoteDetail from '@pages/sales/QuoteDetail';
import QuoteForm from '@pages/sales/QuoteForm';

// Terminal Pages
import TerminalsList from '@pages/terminals/TerminalsList';
import TerminalDetail from '@pages/terminals/TerminalDetail';
import TerminalForm from '@pages/terminals/TerminalForm';

// Lifting Pages
import LiftingsList from '@pages/liftings/LiftingsList';
import LiftingDetail from '@pages/liftings/LiftingDetail';
import LiftingForm from '@pages/liftings/LiftingForm';

// Tax Export Pages
import TaxExports from '@pages/reports/TaxExports';
import ReconciliationReport from '@pages/reports/ReconciliationReport';

// Delivery Programs Pages
import DeliveryPrograms from '@pages/operations/DeliveryPrograms';
import DeliveryProgramDetail from '@pages/operations/DeliveryProgramDetail';
import DeliveryProgramForm from '@pages/operations/DeliveryProgramForm';

// Fleet Maintenance Pages
import Maintenance from '@pages/fleet/Maintenance';
import MaintenanceDetail from '@pages/fleet/MaintenanceDetail';
import MaintenanceForm from '@pages/fleet/MaintenanceForm';

// Admin Pages
import Users from '@pages/admin/Users';
import UserDetail from '@pages/admin/UserDetail';
import UserForm from '@pages/admin/UserForm';
import UserPermissions from '@pages/admin/UserPermissions';
import Roles from '@pages/admin/Roles';
import RoleForm from '@pages/admin/RoleForm';
import ImportExport from '@pages/admin/ImportExport';
import CustomerTypeTemplatesPage from '@pages/admin/CustomerTypeTemplatesPage';
import CustomerTypeTemplateFormPage from '@pages/admin/CustomerTypeTemplateFormPage';

// Styles
import '@styles/index.css';
import '@styles/theme.css';

/**
 * React Query client configuration
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Main Application Component
 */
function App() {
  return (
    /* Outermost net. The route-level boundary inside DashboardLayout handles
       almost everything and keeps the navigation usable; this one exists for
       the cases it cannot reach — the providers, the layout itself, and the
       public routes — so that no failure anywhere produces a white page. */
    <ErrorBoundary label="the application">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PermissionProvider>
        <ThemeProvider>
          <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            {/* Public registration is gone: it self-provisioned an ADMIN account.
                Instances are provisioned by the vendor and users are added by
                admin-issued invitation, which lands here. */}
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />

            {/* Protected routes with dashboard layout */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              {/* Development only — see CrashTestPage. Absent from a production
                  build, and the route with it, so this cannot be reached by a
                  customer. It exists so the end-to-end suite can prove the
                  route-level error boundary catches a render that throws. */}
              {import.meta.env.DEV && (
                <Route path="__crash-test" element={<CrashTestPage />} />
              )}
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/new" element={<CustomerWizard />} />
              <Route path="customers/quick-add" element={<NewCustomerPage />} />
              <Route path="customers/:id" element={<CustomerDetailPage />} />
              {/* Customer Location (Ship-To) Routes */}
              <Route path="customers/:customerId/locations" element={<CustomerLocations />} />
              <Route path="customers/:customerId/locations/new" element={<CustomerLocationWizard />} />
              <Route path="customers/:customerId/locations/:id" element={<CustomerLocationDetail />} />
              <Route path="customers/:customerId/locations/:id/edit" element={<CustomerLocationForm />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="inventory/tax-classes" element={<AdminRoute><TaxClassAssignment /></AdminRoute>} />
              <Route path="inventory/new" element={<InventoryFormPage />} />
              <Route path="inventory/:id" element={<InventoryDetailPage />} />
              <Route path="inventory/:id/edit" element={<InventoryFormPage />} />
              <Route path="tanks" element={<TanksPage />} />
              <Route path="tanks/new" element={<TankFormPage />} />
              <Route path="tanks/:id" element={<TankDetailPage />} />
              <Route path="tanks/:id/edit" element={<TankFormPage />} />
              <Route path="tanks/:id/history" element={<TankHistory />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/new" element={<OrderFormPage />} />
              <Route path="orders/:id" element={<OrderDetailPage />} />
              <Route path="orders/:id/edit" element={<OrderFormPage />} />
              <Route path="dispatch" element={<DispatchPage />} />
              <Route path="dispatch/routes" element={<DriverRoutePage />} />
              <Route path="dispatch/new" element={<DispatchFormPage />} />
              <Route path="dispatch/:id" element={<DispatchDetailPage />} />
              <Route path="dispatch/:id/edit" element={<DispatchFormPage />} />
              <Route path="shipments" element={<ShipmentsPage />} />
              <Route path="shipments/new" element={<NewShipmentPage />} />
              <Route path="shipments/:id" element={<ShipmentDetailPage />} />
              <Route path="trucks" element={<TrucksPage />} />
              <Route path="trucks/new" element={<TruckFormPage />} />
              <Route path="trucks/:id" element={<TruckDetailPage />} />
              <Route path="trucks/:id/edit" element={<TruckFormPage />} />
              <Route path="carriers" element={<CarriersPage />} />
              <Route path="drivers" element={<DriversPage />} />
              <Route path="drivers/new" element={<DriverFormPage />} />
              <Route path="drivers/:id" element={<DriverDetailPage />} />
              <Route path="drivers/:id/edit" element={<DriverFormPage />} />
              <Route path="suppliers" element={<SuppliersPage />} />
              <Route path="suppliers/new" element={<SupplierFormPage />} />
              <Route path="suppliers/:id" element={<SupplierDetailPage />} />
              <Route path="suppliers/:id/edit" element={<SupplierFormPage />} />
              <Route path="receipts" element={<ReceiptsPage />} />
              <Route path="receipts/new" element={<ReceiptFormPage />} />
              <Route path="receipts/:id" element={<ReceiptDetailPage />} />
              <Route path="receipts/:id/edit" element={<ReceiptFormPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="invoices/new" element={<InvoiceFormPage />} />
              <Route path="invoices/:id" element={<InvoiceDetailPage />} />
              <Route path="invoices/:id/edit" element={<InvoiceFormPage />} />
              <Route path="ap" element={<AccountsPayablePage />} />
              <Route path="ap/new" element={<BillFormPage />} />
              <Route path="ap/:id" element={<BillDetailPage />} />
              <Route path="ap/:id/edit" element={<BillFormPage />} />
              <Route path="accounting/accounts-receivable" element={<AccountsReceivablePage />} />
              <Route path="financials" element={<FinancialsPage />} />
              {/* Pricing Routes */}
              <Route path="pricing/pricebooks" element={<PriceBooksPage />} />
              <Route path="pricing/pricebooks/new" element={<PriceBookFormPage />} />
              <Route path="pricing/pricebooks/:id" element={<PriceBookDetailPage />} />
              <Route path="pricing/pricebooks/:id/edit" element={<PriceBookFormPage />} />
              <Route path="pricing/rackprices" element={<RackPricesPage />} />
              <Route path="pricing/terminal-rack-prices" element={<TerminalRackPricesPage />} />
              <Route path="pricing/contracts" element={<ContractsPage />} />
              <Route path="pricing/contracts/new" element={<ContractFormPage />} />
              <Route path="pricing/contracts/:id" element={<ContractDetailPage />} />
              <Route path="pricing/contracts/:id/edit" element={<ContractFormPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/appearance" element={<AppearanceSettingsPage />} />
              {/* Credit Management Routes */}
              <Route path="credit" element={<CreditManagementPage />} />
              {/* Tax Management Routes */}
              <Route path="settings/tax" element={<TaxRatesPage />} />
              <Route path="settings/tax/configuration" element={<AdminRoute><TaxConfiguration /></AdminRoute>} />
              <Route path="settings/tax/readiness" element={<TaxReadiness />} />
              <Route path="settings/tax/new" element={<TaxRateFormPage />} />
              <Route path="settings/tax/:id" element={<TaxRateFormPage />} />
              {/* BOL Routes */}
              <Route path="bol" element={<BOLListPage />} />
              <Route path="bol/:id" element={<BOLDetailPage />} />
              <Route path="bol/:id/print" element={<BOLPrintPage />} />
              {/* Reports Routes */}
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/ar-aging" element={<ARAgingReport />} />
              <Route path="reports/sales" element={<SalesReport />} />
              <Route path="reports/delivery" element={<DeliveryReport />} />
              <Route path="reports/tax" element={<TaxReport />} />
              <Route path="reports/statements" element={<StatementsPage />} />
              <Route path="reports/statements/:id" element={<StatementDetail />} />
              <Route path="reports/statements/:id/print" element={<StatementPrint />} />
              {/* Email Routes */}
              <Route path="settings/email/templates" element={<EmailTemplates />} />
              <Route path="settings/email/templates/new" element={<EmailTemplateForm />} />
              <Route path="settings/email/templates/:id" element={<EmailTemplateForm />} />
              <Route path="settings/email/logs" element={<EmailLogs />} />
              <Route path="settings/email" element={<EmailSettingsPage />} />
              {/* Audit & System Settings Routes */}
              <Route path="settings/audit" element={<AuditLogs />} />
              <Route path="settings/system" element={<SystemSettings />} />
              <Route path="settings/configuration" element={<ConfigurationSettings />} />
              {/* Sales / CRM Routes */}
              <Route path="sales" element={<SalesDashboard />} />
              <Route path="sales/leads" element={<LeadsList />} />
              <Route path="sales/leads/new" element={<LeadForm />} />
              <Route path="sales/leads/:id" element={<LeadDetail />} />
              <Route path="sales/leads/:id/edit" element={<LeadForm />} />
              <Route path="sales/activities" element={<ActivitiesList />} />
              <Route path="sales/quotes" element={<QuotesList />} />
              <Route path="sales/quotes/new" element={<QuoteForm />} />
              <Route path="sales/quotes/:id" element={<QuoteDetail />} />
              <Route path="sales/quotes/:id/edit" element={<QuoteForm />} />
              {/* Terminal Routes */}
              <Route path="terminals" element={<TerminalsList />} />
              <Route path="terminals/new" element={<TerminalForm />} />
              <Route path="terminals/:id" element={<TerminalDetail />} />
              <Route path="terminals/:id/edit" element={<TerminalForm />} />
              {/* Lifting Routes */}
              <Route path="liftings" element={<LiftingsList />} />
              <Route path="liftings/new" element={<LiftingForm />} />
              <Route path="liftings/:id" element={<LiftingDetail />} />
              <Route path="liftings/:id/edit" element={<LiftingForm />} />
              {/* Tax Exports Route */}
              <Route path="reports/tax-exports" element={<TaxExports />} />
              <Route path="reports/reconciliation" element={<ReconciliationReport />} />
              {/* Delivery Programs Routes */}
              <Route path="operations/delivery-programs" element={<DeliveryPrograms />} />
              <Route path="operations/delivery-programs/new" element={<DeliveryProgramForm />} />
              <Route path="operations/delivery-programs/:id" element={<DeliveryProgramDetail />} />
              <Route path="operations/delivery-programs/:id/edit" element={<DeliveryProgramForm />} />
              {/* Fleet Maintenance Routes */}
              <Route path="fleet/maintenance" element={<Maintenance />} />
              <Route path="fleet/maintenance/new" element={<MaintenanceForm />} />
              <Route path="fleet/maintenance/:id" element={<MaintenanceDetail />} />
              <Route path="fleet/maintenance/:id/edit" element={<MaintenanceForm />} />
              {/* Admin Routes.
                  The user LIST stays readable by anyone signed in — the API
                  allows that read, and knowing who your colleagues are is not
                  privileged. Everything that CHANGES access is admin-gated
                  here and, more importantly, by `requireAdmin` on the server. */}
              <Route path="admin/users" element={<Users />} />
              <Route path="admin/users/:id" element={<UserDetail />} />
              <Route path="admin/users/new" element={<AdminRoute><UserForm /></AdminRoute>} />
              <Route path="admin/users/:id/edit" element={<AdminRoute><UserForm /></AdminRoute>} />
              <Route path="admin/users/:id/permissions" element={<AdminRoute><UserPermissions /></AdminRoute>} />
              <Route path="admin/roles" element={<AdminRoute><Roles /></AdminRoute>} />
              <Route path="admin/roles/new" element={<AdminRoute><RoleForm /></AdminRoute>} />
              <Route path="admin/roles/:id/edit" element={<AdminRoute><RoleForm /></AdminRoute>} />
              <Route path="admin/import-export" element={<AdminRoute><ImportExport /></AdminRoute>} />
              {/* Customer Type Templates Routes */}
              <Route path="admin/customer-type-templates" element={<CustomerTypeTemplatesPage />} />
              <Route path="admin/customer-type-templates/new" element={<CustomerTypeTemplateFormPage />} />
              <Route path="admin/customer-type-templates/:id" element={<CustomerTypeTemplateFormPage />} />
            </Route>

            {/* 404 fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </BrowserRouter>
        </ThemeProvider>
        </PermissionProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
